import React, { ReactElement } from 'react'
import { GuideComponent } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/GuideComponent.types'
import styles from '@/components/static/StudyMaterialsGuidePage/StudyMaterialsGuidePage.module.scss'
import { reusableCssClass } from '@/utils/reusableCssClasses'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import { LinkItem } from '@/components/static/ui/LinkItem/LinkItem'
import { BaseGuideComponentNoImage } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/BaseGuideComponentNoImage/BaseGuideComponentNoImage'
import { BaseGuideComponent } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/BaseGuideComponent/BaseGuideComponent'
import { Screenshot } from '@/components/static/images/Screenshot/Screenshot'
import { StudyBuddy } from '@/components/static/images/owlBranding/studyBuddy'
import { BackToTop } from '@/components/static/BackToTop/BackToTop'
import { ClickableArrow } from '@/components/ClickableArrow/ClickableArrow'
import Tooltip from 'antd/lib/tooltip'
import {
    getAbbreviationName,
    getLongName,
} from '@/components/static/StudyMaterialsGuidePage/utils'
import { CourseConfigType } from 'common/src/courseConfig'

export const OtherGuidePage: React.FC<GuideComponent> = (
    props
): ReactElement => {
    return (
        <div className={styles.studyMaterialsGuidePageBackground}>
            <BackToTop isLoggedIn={props.isLoggedIn} />
            <div className={styles.studyMaterialsGuidePage}>
                <div className={styles.headerContainer}>
                    <div className={reusableCssClass.centerChildrenVertically}>
                        <Tooltip
                            title={'Return to Overview'}
                            placement={'right'}
                        >
                            <ClickableArrow
                                orientation={'left'}
                                linkDestination={
                                    UiLayoutConstant.STUDY_MATERIALS_GUIDE_PAGE
                                }
                            />
                        </Tooltip>
                    </div>
                </div>
                <div className={styles.title}>OTHER EXCITING OFFERINGS</div>
                <BaseGuideComponentNoImage
                    text={
                        <div className={styles.nonBulletSection}>
                            This section highlights unique features of&nbsp;
                            {getAbbreviationName(props.courseConfigType)} that
                            we have added based on a combination of user
                            feedback and a desire to improve the study
                            experience as a whole. For a full list of recent
                            additions, visit our{' '}
                            <LinkItem
                                href={{
                                    pathname: UiLayoutConstant.UPDATES_PAGE,
                                }}
                                inline={true}
                            >
                                Updates Page
                            </LinkItem>
                            .
                        </div>
                    }
                />
                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                Eupheus, Your AI Study Partner
                            </div>
                            Every student gets Eupheus, an AI study partner
                            powered by Claude. Eupheus doesn&apos;t answer from
                            memory — it researches your actual course materials
                            first, then shows you what it used. Eupheus can:
                            <ul>
                                <li>
                                    Answer questions about anything on the
                                    syllabus, and link the exact notecard,
                                    outline section, or question behind the
                                    answer so you can check its work
                                </li>
                                <li>
                                    Hold a real conversation — ask follow-ups,
                                    and pick any saved conversation back up
                                    later
                                </li>
                                <li>
                                    See the page you&apos;re on (&quot;which
                                    notecard is this?&quot;) and take you where
                                    you ask to go
                                </li>
                                <li>
                                    Grade any practice question you&apos;ve
                                    answered — including an Excel workbook you
                                    upload — with point-by-point feedback
                                </li>
                            </ul>
                            Eupheus sticks to your course and will politely pass
                            on anything off-syllabus. It can still make
                            mistakes, which is exactly why every answer keeps
                            its sources one click away.
                            {props.courseConfigType ===
                                CourseConfigType.fsaHealth && (
                                <p>
                                    <LinkItem
                                        href={{
                                            pathname:
                                                UiLayoutConstant.EUPHEUS_PAGE,
                                        }}
                                        inline={true}
                                    >
                                        Read more about what Eupheus can do
                                    </LinkItem>
                                </p>
                            )}
                        </div>
                    }
                    nonBullet={
                        <Screenshot
                            src={
                                '/updates/20260819/formulaTableScreenshot.webp'
                            }
                            alt={
                                'screenshot of Eupheus explaining a formula with a worked example table and linked sources'
                            }
                        />
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                Instantaneous Search
                            </div>
                            <p>
                                A benefit of digital content is that it can be
                                efficiently searched. Search can be accessed
                                from the top right of any page. Search results
                                are broken out into the three pillars of
                                content: outlines, notecards, and practice
                                problems. Clicking on the search result will
                                load that piece of content. For notecards and
                                practice problems, the piece of content will be
                                shown on its own, outside a deck / problem set,
                                but it can be added to a deck / problem set via
                                a button at the bottom of the page.
                            </p>
                        </div>
                    }
                    nonBullet={
                        <Screenshot
                            src={'/studyMaterialsGuide/searchScreenshot.webp'}
                            alt={'Screenshot of general search'}
                        />
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                Personalized Study Recommendations
                            </div>
                            <p>
                                Take the guesswork out of studying. By tracking
                                your progress and analyzing your study patterns,
                                we are able to provide you with personalized
                                recommendations on what to study next.
                            </p>
                        </div>
                    }
                    nonBullet={
                        <>
                            <Screenshot
                                src={
                                    '/splashPage/middleSection/studyPlanDashboardScreenshot.webp'
                                }
                                alt={'Recommendation screenshot'}
                            />
                        </>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Study Timer</div>
                            <p>
                                {getLongName(props.courseConfigType)} offers a
                                study time tracking tool that allows you to keep
                                track of how long you have studied each day. We
                                run the timer for you automatically - you can
                                pause and restart the timer at your choosing. If
                                you forget to pause the timer and step away, the
                                timer will automatically pause itself after a
                                period of inactivity.
                            </p>
                            {props.courseConfigType ===
                                CourseConfigType.fsaHealth && (
                                <p>
                                    The general recommendation is 100 hours of
                                    studying per hour of exam - so for GH101
                                    which is 3 hours, the suggested study time
                                    is 300 hours. The tracker helps keeps
                                    students accountable for how much time is
                                    actually spent studying.{' '}
                                </p>
                            )}
                        </div>
                    }
                    nonBullet={
                        <>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/studyTimeDashboardScreenshot.webp'
                                }
                                alt={'Study time dashboard screenshot'}
                            />
                        </>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                Study Buddy System
                            </div>
                            <p>
                                Students with a paid subscription can sign up to
                                be paired with another student as study buddies
                                for an exam sitting. We match on various
                                criteria including timezone, desired meeting
                                frequency, and study styles so that we can
                                ensure the best fit between buddies.
                            </p>
                            The study buddy systems helps by:
                            <ul>
                                <li>
                                    Provides a unique opportunity to practicing
                                    explaining concepts to one another
                                </li>
                                <li>
                                    Act as a support network, keeping each other
                                    on track with study goals
                                </li>
                            </ul>
                        </div>
                    }
                    nonBullet={<StudyBuddy />}
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                Instructor Access
                            </div>
                            <p>
                                With a subscription to&nbsp;
                                {getAbbreviationName(props.courseConfigType)},
                                you never have to study alone. As a student, you
                                can leave a question on any outline, notecard,
                                or question and your instructor will get back to
                                you within 24hours. You can also use the
                                &quot;Contact Instructor&quot; button located on
                                every page to ask your instructor a question.
                            </p>
                            <p>
                                Questions can include, but are not limited to:
                            </p>
                            <ul>
                                <li>Questions about the content</li>
                                <li>Errata in the content</li>
                                <li>
                                    Feedback about issues with the website or
                                    areas of improvement
                                </li>
                                <li>
                                    Questions about study habits and strategies
                                </li>
                                <li>Career-related questions</li>
                            </ul>
                        </div>
                    }
                    nonBullet={
                        <Screenshot
                            src={
                                '/studyMaterialsGuide/contactInstructorModalScreenshot.webp'
                            }
                            alt={'Screenshot of contact instructor feature'}
                        />
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
            </div>
        </div>
    )
}
